import React, { useEffect, ReactNode, useState, useCallback, useMemo } from 'react'
import { Formik, Form, useFormikContext, useField } from 'formik'
import CreateTeam from './CreateTeam'
import CreateMember from './CreateMember'
import CreateBrand from './CreateBrand'
import RemoveAndDeleteMember from './RemoveOrDeleteMember'
import DeleteTeam from './DeleteTeam'
import { TeamState } from 'state/teams'
import { ObjectSchema } from 'yup'
import Text from 'particles/Text'
import Field from 'components/Shared/Fields'
import { ModalContainer } from './style'
import styled from 'particles/themes/styled'
import DeleteBrand from './DeleteBrand'
import RectangleButton from 'particles/Button/RectangleButton'
import Dropdown, { OptionsState } from 'particles/Dropdown'
import MenuItem from 'particles/MenuItem'
import { useTranslation } from 'react-i18next'
import { EmptySearchResult } from 'particles/MenuItem/Search'
import { FormikTextArea } from 'particles/TextInputs/TextArea'
import { ModalProps as BaseModalProps } from 'particles/Modal'
import { LabelSize } from 'particles/Label'
import EditTeam from './EditTeam'

export enum MODAL_TYPE {
    CREATE_TEAM = 'createTeam',
    EDIT_TEAM_TYPE = 'editTeamType',
    EDIT_TEAM_NAME = 'editTeamName',
    DELETE_TEAM = 'deleteTeam',
    CREATE_MEMBER = 'createMember',
    EDIT_MEMBER = 'editMember',
    DELETE_MEMBER = 'deleteMember',
    REMOVE_TEAM_MEMBER = 'removeTeamMember',
    CREATE_BRAND = 'createBrand',
    EDIT_BRAND = 'editBrand',
    DELETE_BRAND = 'deleteBrand',
}
export interface ModalState {
    type: MODAL_TYPE | undefined
    team: TeamState | undefined
}

export interface AddMemberValuesState {
    name: string
    email: string
    role: string
    business_type?: string
}
export interface CreateTeamValuesState {
    name?: string
    service_fee_percentage?: number
    type?: string
    reset_role_permissions?: boolean
    ambassador_enabled?: boolean
    is_ai_caption_review_enabled?: boolean
}
export interface CreateBrandValuesState {
    name: string
    description: string
    country: string
    industry: string | null
    industry_others?: string | null
    member_range?: string | null
    logo_image: {
        image: string
        uuid: string
        img_main: string
    }[]
}

interface ModalDropDownSelectorProps {
    title: string
    subtitle?: string
    name: string
    options: OptionsState[]
    placeholder?: string
    isDisabled?: boolean
    customInputCondition?: (values: any) => void
    customInputName?: string
    isRequired?: boolean
    isSearchable?: boolean
    className?: string
    dataTestId?: string
    labelSize?: LabelSize
}

const DropDownContainer = styled.div`
    display: flex;
    flex-direction: column;
    gap: ${({ theme }) => theme.spacing[200]};
`

const ModalDropDownSelector: React.FC<ModalDropDownSelectorProps> = ({
    placeholder,
    title,
    subtitle,
    name,
    options,
    isDisabled,
    customInputCondition,
    customInputName,
    isRequired,
    isSearchable,
    className,
    dataTestId,
    labelSize,
}) => {
    const [t] = useTranslation()
    const { values, setFieldValue } = useFormikContext<any>()
    const [, meta] = useField(name)
    const isError = meta.touched && Boolean(meta.error)
    const showCustomInput = customInputCondition && customInputCondition(values)
    const selectedOption = options.find((option: OptionsState) => {
        return option.value === values[name]
    })
    const [searchValue, setSearchValue] = useState<string>('')

    const filteredOptions = useMemo(() => {
        if (!searchValue) {
            return options
        }
        return options.filter((option: OptionsState) => {
            return option.label.toLowerCase().includes(searchValue.toLowerCase())
        })
    }, [searchValue, options])

    const handleSearchValueChange = useCallback((value: string) => {
        setSearchValue(value)
    }, [])

    const handleClearSearch = useCallback(() => {
        setSearchValue('')
    }, [])
    return (
        <DropDownContainer>
            <Dropdown
                label={title}
                subtext={subtitle}
                isDisabled={isDisabled}
                placeholder={placeholder}
                isRequired={isRequired}
                value={selectedOption?.value}
                errorMessage={(isError && meta.error) || undefined}
                onChange={(value): void => {
                    setFieldValue(name, value)
                }}
                renderValue={() => <Text scale="bodyS400">{selectedOption?.label}</Text>}
                maxWidth="100%"
                className={className}
                dataTestId={dataTestId}
                labelSize={labelSize}
            >
                {isSearchable && (
                    <MenuItem.Search
                        onSearch={handleSearchValueChange}
                        onClearSearch={handleClearSearch}
                        placeholder={t('placeholderText.searchIndustry')}
                        searchValue={searchValue}
                        size="s"
                    />
                )}
                {filteredOptions.length > 0 ? (
                    filteredOptions.map((option, index) => (
                        <MenuItem.Text
                            key={`dropdown-${index}`}
                            tabIndex={index}
                            text={option.label}
                            value={option.value}
                        />
                    ))
                ) : (
                    <EmptySearchResult />
                )}
            </Dropdown>
            {showCustomInput && customInputName && <Field.TextInputField name={customInputName} width="100%" />}
        </DropDownContainer>
    )
}
export type ModalSize = 's' | 'm' | 'l'
interface ModalProps extends BaseModalProps {
    onClose: () => void
    initialValues: AddMemberValuesState | CreateTeamValuesState | CreateBrandValuesState
    validationSchema: ObjectSchema<any>
    onSubmit: (value: any, action?: any) => void
    submitButtonText: string
    cancelButtonText?: string
    children: ReactNode
    isLoading?: boolean
    loadingText?: string
    isDisabled?: boolean
}

const Modal = ({
    onClose,
    initialValues,
    validationSchema,
    onSubmit,
    submitButtonText,
    cancelButtonText,
    subtext,
    title,
    children,
    isLoading = false,
    loadingText,
    size = 'm',
    isDisabled = false,
    isClosable = true,
    ...props
}: ModalProps): JSX.Element => {
    const [t] = useTranslation()

    const handleSearchMode = (e: any): void => {
        if (e.target.id === 'modal-container') {
            onClose()
        }
    }

    useEffect(() => {
        if (!isClosable) return
        window.addEventListener('click', handleSearchMode)
        return (): void => window.removeEventListener('click', handleSearchMode)
    }, [])

    return (
        <ModalContainer
            size={size}
            hasButtonSet={false}
            hasIcon={false}
            title={title}
            subtext={subtext}
            onClose={() => {
                if (isLoading) return
                onClose()
            }}
            isClosable={isClosable}
            {...props}
        >
            <Formik
                enableReinitialize={true}
                initialValues={initialValues}
                validationSchema={validationSchema}
                onSubmit={onSubmit}
            >
                {(formikBag): JSX.Element => {
                    return (
                        <Form className="popup-content">
                            <div className="content">
                                {children}
                                <div className="button-group">
                                    {isClosable && (
                                        <RectangleButton
                                            size="l"
                                            form="outlined"
                                            label={cancelButtonText ?? t('button.cancel')}
                                            onClick={e => {
                                                if (isLoading) return
                                                e.preventDefault()
                                                onClose()
                                            }}
                                            isLoading={isLoading}
                                            loadingText={loadingText}
                                            isDisabled={isDisabled}
                                        />
                                    )}
                                    <RectangleButton
                                        size="l"
                                        type="submit"
                                        form="filled"
                                        label={submitButtonText}
                                        onClick={e => {
                                            e.preventDefault()
                                            formikBag.submitForm()
                                        }}
                                        isLoading={isLoading}
                                        loadingText={loadingText}
                                        isDisabled={isDisabled}
                                        className="submit-button"
                                    />
                                </div>
                            </div>
                        </Form>
                    )
                }}
            </Formik>
        </ModalContainer>
    )
}

Modal.TextInput = Field.TextInputField
Modal.Radios = Field.RadioGroupField
Modal.DropDownSelector = ModalDropDownSelector
Modal.ImageUploader = Field.ImageUploadField
Modal.TextArea = FormikTextArea

export default Modal

const PredefinedModal: React.FC<any> = ({ type, ...props }) => {
    switch (type) {
        case MODAL_TYPE.CREATE_TEAM:
            return <CreateTeam {...props} type={type} />
        case MODAL_TYPE.EDIT_TEAM_NAME:
        case MODAL_TYPE.EDIT_TEAM_TYPE:
            return <EditTeam {...props} type={type} />
        case MODAL_TYPE.CREATE_MEMBER:
        case MODAL_TYPE.EDIT_MEMBER:
            return <CreateMember {...props} type={type} />
        case MODAL_TYPE.CREATE_BRAND:
        case MODAL_TYPE.EDIT_BRAND:
            return <CreateBrand {...props} type={type} />
        case MODAL_TYPE.DELETE_TEAM:
            return <DeleteTeam {...props} type={type} />
        case MODAL_TYPE.REMOVE_TEAM_MEMBER:
        case MODAL_TYPE.DELETE_MEMBER:
            return <RemoveAndDeleteMember {...props} type={type} />
        case MODAL_TYPE.DELETE_BRAND:
            return <DeleteBrand {...props} type={type} />
        default:
            return <></>
    }
}

export { PredefinedModal }
